import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { MagnifyingGlass, ChatCircleText, Warning, Siren, Lock, PencilSimple, WhatsappLogo, MessengerLogo, InstagramLogo, CircleNotch, CheckCircle, Info, Robot, Phone } from "@phosphor-icons/react";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SLA_MINUTES = 15;
// Cap how OLD a conversation can be and still count as "overdue". Historic
// chats from days/weeks ago (where the customer's last msg was inbound and
// then the chat went silent) would otherwise stay flagged forever and
// inflate the badge to hundreds. We only highlight the recent backlog so
// agents focus on actionable threads.
const OVERDUE_MAX_AGE_HOURS = 24;

export function isOverdue(contact, nowMs = Date.now()) {
  if (!contact) return false;
  if (contact.status === "resolved") return false;
  if (contact.last_direction !== "inbound") return false;
  const ts = contact.last_timestamp;
  if (!ts) return false;
  const t = new Date(ts).getTime();
  if (Number.isNaN(t)) return false;
  const ageMs = nowMs - t;
  if (ageMs > OVERDUE_MAX_AGE_HOURS * 60 * 60 * 1000) return false;
  return ageMs > SLA_MINUTES * 60 * 1000;
}

export function isActiveComplaint(contact) {
  return !!(contact && contact.complaint && contact.complaint.active);
}

function initials(name, phone) {
  const src = (name || phone || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function shortDistance(date) {
  if (!date) return "";
  const ms = Date.now() - date.getTime();
  if (Number.isNaN(ms)) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo`;
  const yr = Math.floor(day / 365);
  return `${yr}y`;
}

// Verbose "5 min ago" / "2 hr ago" — shown in the dedicated meta row of
// each conversation tile so the agent can see *at a glance* how stale
// the chat is. Per user request 2026-02-11 — "lst conversation kab huwa
// hai uska time bhi dikhaiye".
function relativeAgo(date) {
  if (!date) return "";
  const ms = Date.now() - date.getTime();
  if (Number.isNaN(ms) || ms < 0) return "";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day > 1 ? "s" : ""} ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk} wk${wk > 1 ? "s" : ""} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} mo ago`;
  const yr = Math.floor(day / 365);
  return `${yr} yr ago`;
}

function truncateWords(text, limit = 4) {
  if (!text) return "";
  const parts = String(text).trim().split(/\s+/);
  if (parts.length <= limit) return parts.join(" ");
  return parts.slice(0, limit).join(" ") + "…";
}

// Detect a contact's source channel from prefix on `phone` field, or
// fall back to an explicit `channel` attribute set on the document.
// Returns {key, label, badge, color, iconText} for rendering.
function channelMeta(contact) {
  const ch = contact?.channel;
  const phone = contact?.phone || "";
  if (ch === "messenger" || phone.startsWith("fb:")) {
    return {
      key: "messenger",
      label: "Messenger",
      iconText: "M",
      badgeBg: "bg-[#0084FF]",
      badgeText: "text-white",
      addressLabel: "via Messenger",
    };
  }
  if (ch === "instagram" || phone.startsWith("ig:")) {
    return {
      key: "instagram",
      label: "Instagram",
      iconText: "IG",
      badgeBg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
      badgeText: "text-white",
      addressLabel: "via Instagram",
    };
  }
  return {
    key: "whatsapp",
    label: "WhatsApp",
    iconText: "W",
    badgeBg: "bg-[#25D366]",
    badgeText: "text-white",
    addressLabel: null, // we render `+phone` for whatsapp
  };
}

const STATUS_DOT = {
  new: "bg-[#F59E0B]",
  in_progress: "bg-[#3B82F6]",
  resolved: "bg-[#10B981]",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "complaint", label: "Complaint", urgent: true },
  { key: "call_request", label: "Call", urgent: true },
  { key: "overdue", label: "Overdue" },
  { key: "mine", label: "Mine" },
  { key: "new", label: "New" },
  { key: "in_progress", label: "Active" },
  { key: "resolved", label: "Done" },
];

export default function Sidebar({
  contacts,
  counts,
  selected,
  onSelect,
  search,
  setSearch,
  filter,
  setFilter,
  currentUserId,
  typingByPhone = {},
  viewersByPhone = {},
  claimsByPhone = {},
  onSearchResults,
  mobileHidden = false,
}) {
  const { user: meUser } = useAuth();
  const isAdmin = (meUser?.role || "").toLowerCase() === "admin";
  // Re-render every 30s so "overdue" state updates as time passes
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Server-side contact search — fires only when the local query is >= 2
  // chars and debounces user typing by 300ms. Keeps the inbox snappy when
  // the customer base grows beyond what we can ship to the browser. Results
  // get merged into contactsMeta via onSearchResults so the existing client
  // filter immediately surfaces them.
  const [searchLoading, setSearchLoading] = useState(false);
  useEffect(() => {
    if (!onSearchResults) return;
    const q = (search || "").trim();
    if (q.length < 2) {
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.get(
          `/contacts/search?q=${encodeURIComponent(q)}&limit=50`,
        );
        onSearchResults(r.data?.contacts || []);
      } catch (_) {
        // silent — sidebar still shows whatever local matches we have
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [search, onSearchResults]);

  // Quick ride-status peek — yellow "i" icon next to each contact name.
  // Calls /api/contacts/{phone}/rodbez-ride-status which hits RodBez live
  // and returns the same status block the bot would send to the customer.
  // Useful for triaging complaint chats before opening them.
  const [rideStatus, setRideStatus] = useState(null);
  // {open, loading, error, contact, data, text}
  const openRideStatus = async (e, contact) => {
    e.stopPropagation();
    e.preventDefault();
    setRideStatus({ open: true, loading: true, contact, data: null, text: "", error: null });
    try {
      const r = await api.get(
        `/contacts/${encodeURIComponent(contact.phone)}/rodbez-ride-status`,
      );
      setRideStatus({
        open: true,
        loading: false,
        contact,
        data: r.data?.found ? r.data.data : null,
        text: r.data?.text || "",
        error: null,
      });
      // Optimistic local update — immediately push the freshly-fetched
      // status into the contacts map so the sidebar's department chip
      // (Sales / Operations / New) reflects reality without waiting for
      // the SSE event roundtrip. The SSE delta will arrive seconds later
      // and reconcile any tweaks (e.g. department flag from the 24h
      // Completed-window rule).
      if (onSearchResults) {
        if (r.data?.found && r.data?.data) {
          const d = r.data.data;
          onSearchResults([{
            phone: contact.phone,
            rodbez_last_ride_status: d.status || "",
            rodbez_last_ride_id: d.bookingNo || null,
            rodbez_last_ride_status_updated_at: new Date().toISOString(),
          }]);
        } else {
          // No ride at all — reset cache fields so chip flips to "New"
          onSearchResults([{
            phone: contact.phone,
            rodbez_last_ride_status: null,
            rodbez_last_ride_id: null,
            rodbez_last_ride_status_updated_at: null,
          }]);
        }
      }
    } catch (err) {
      setRideStatus({
        open: true,
        loading: false,
        contact,
        data: null,
        text: "",
        error: err?.response?.data?.detail || "Failed to fetch ride status",
      });
    }
  };

  // Channel filter — default "whatsapp" (most traffic). Persist user choice.
  const [channelFilter, setChannelFilter] = useState(() => {
    try {
      return localStorage.getItem("rb_channel_filter") || "whatsapp";
    } catch {
      return "whatsapp";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("rb_channel_filter", channelFilter);
    } catch {}
  }, [channelFilter]);

  // When user switches the channel tab, deselect the currently open chat if
  // it doesn't belong to the new channel — otherwise the previously open
  // (e.g. WhatsApp) thread stays visible while the inbox list shows
  // Messenger contacts, which is confusing.
  useEffect(() => {
    if (!selected || channelFilter === "all") return;
    const activeContact = contacts.find((c) => c.phone === selected);
    if (!activeContact) return;
    if (channelMeta(activeContact).key !== channelFilter) {
      onSelect(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelFilter]);

  // Apply channel filter on top of all other filters/search.
  const visibleContacts = contacts.filter((c) => {
    if (channelFilter === "all") return true;
    return channelMeta(c).key === channelFilter;
  });

  // Per-channel counts (computed on full contact list, not filtered).
  // Skip rows that have no real activity (no last_timestamp) so the channel
  // pill numbers match the visible inbox.
  const channelCounts = contacts.reduce(
    (acc, c) => {
      if (!c.last_timestamp) return acc;
      const k = channelMeta(c).key;
      acc[k] = (acc[k] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, whatsapp: 0, messenger: 0, instagram: 0 },
  );

  // Per-channel UNREAD counts — drives the red badge on each channel pill.
  // A contact contributes to unread if its `unread` counter is >0.
  const channelUnread = contacts.reduce(
    (acc, c) => {
      const u = Number(c.unread) || 0;
      if (u <= 0) return acc;
      const k = channelMeta(c).key;
      acc[k] = (acc[k] || 0) + u;
      acc.all += u;
      return acc;
    },
    { all: 0, whatsapp: 0, messenger: 0, instagram: 0 },
  );

  return (
    <aside
      className={`w-full md:w-[400px] md:shrink-0 h-full border-r border-[var(--border)] bg-[var(--surface)] flex-col ${
        mobileHidden ? "hidden md:flex" : "flex"
      }`}
      data-testid="sidebar"
    >
      <div className="px-4 py-3 border-b border-[var(--border)] space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] tracking-[0.14em] uppercase text-[var(--muted)]">Inbox</div>
          {/* Channel filter pills — All / WA / Messenger / Instagram */}
          <div
            className="inline-flex items-center gap-0.5 rounded-md border border-[var(--border)] p-0.5"
            data-testid="channel-filter-pills"
          >
            {[
              { k: "whatsapp", Icon: WhatsappLogo, bg: "bg-[#25D366]", textActive: "text-white", title: "WhatsApp" },
              { k: "messenger", Icon: MessengerLogo, bg: "bg-[#0084FF]", textActive: "text-white", title: "Messenger" },
              { k: "instagram", Icon: InstagramLogo, bg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]", textActive: "text-white", title: "Instagram" },
              { k: "all", Icon: null, label: "All", bg: "bg-[var(--fg)]", textActive: "text-[var(--bg)]", title: "All channels" },
            ].map((opt) => {
              const isActive = channelFilter === opt.k;
              const c = channelCounts[opt.k] || 0;
              const u = channelUnread[opt.k] || 0;
              const Icon = opt.Icon;
              return (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setChannelFilter(opt.k)}
                  data-testid={`channel-filter-${opt.k}`}
                  title={
                    u > 0
                      ? `${opt.title} · ${u} unread`
                      : opt.title
                  }
                  className={`relative px-2 py-1 rounded-[4px] text-[10px] font-bold transition flex items-center gap-1 ${
                    isActive
                      ? `${opt.bg} ${opt.textActive}`
                      : "text-[var(--muted)] hover:text-[var(--text)]"
                  }`}
                >
                  {Icon ? (
                    <Icon size={14} weight={isActive ? "fill" : "regular"} />
                  ) : (
                    <span>{opt.label}</span>
                  )}
                  <span className={`text-[9px] ${isActive ? "opacity-90" : "opacity-60"}`}>
                    {c}
                  </span>
                  {u > 0 && !isActive && (
                    <span
                      className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF4444] text-white text-[9px] font-bold flex items-center justify-center shadow-sm ring-1 ring-[var(--surface)]"
                      data-testid={`channel-unread-${opt.k}`}
                    >
                      {u > 99 ? "99+" : u}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer…"
            className="pl-9 pr-8 h-9 bg-[var(--bg)] border-[var(--border)]"
            data-testid="sidebar-search-input"
          />
          {searchLoading && (
            <CircleNotch
              size={14}
              className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)]"
              data-testid="sidebar-search-loading"
            />
          )}
        </div>
        <div className="flex items-center gap-0.5 text-[11px] flex-wrap">
          {FILTERS.map((f) => {
            const count = counts?.[f.key] ?? 0;
            const isOverdueTab = f.key === "overdue";
            const isComplaintTab = f.key === "complaint";
            const isCallTab = f.key === "call_request";
            const active = filter === f.key;
            const overdueAlert = isOverdueTab && count > 0 && !active;
            const complaintAlert = isComplaintTab && count > 0 && !active;
            const callAlert = isCallTab && count > 0 && !active;
            const complaintActive = isComplaintTab && active;
            const overdueActive = isOverdueTab && active && count > 0;
            const callActive = isCallTab && active && count > 0;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-1.5 py-0.5 rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap ${
                  complaintActive
                    ? "bg-[#DC2626] text-white shadow-sm"
                    : overdueActive
                    ? "bg-[#EF4444] text-white"
                    : callActive
                    ? "bg-[#DC2626] text-white shadow-sm"
                    : active
                    ? "bg-[var(--fg)] text-[var(--bg)]"
                    : complaintAlert
                    ? "bg-[#FEE2E2] text-[#B91C1C] font-semibold hover:bg-[#FECACA] complaint-chip-pulse"
                    : callAlert
                    ? "bg-[#FEE2E2] text-[#B91C1C] font-semibold hover:bg-[#FECACA] complaint-chip-pulse"
                    : overdueAlert
                    ? "bg-[#FEE2E2] text-[#B91C1C] font-semibold hover:bg-[#FECACA]"
                    : "hover:bg-[var(--bg)] text-[var(--muted)]"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {isComplaintTab && <Siren size={10} weight="fill" />}
                {isCallTab && <Phone size={10} weight="fill" />}
                {f.label}
                {count > 0 && (
                  <span className="opacity-80 ml-0.5 tabular-nums">{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <ScrollArea className="flex-1 scroll-thin">
        {visibleContacts.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--muted)] flex flex-col items-center gap-2">
            <ChatCircleText size={28} className="text-[var(--border)]" />
            {channelFilter === "all"
              ? "No conversations."
              : `No ${channelFilter} conversations.`}
            {channelFilter !== "all" && (
              <button
                type="button"
                onClick={() => setChannelFilter("all")}
                className="text-xs underline text-[var(--fg)]"
                data-testid="channel-filter-reset"
              >
                Show all channels
              </button>
            )}
          </div>
        ) : (
          <ul className="py-1">
            {visibleContacts.map((c) => {
              const active = c.phone === selected;
              const mine =
                !!currentUserId &&
                c.assigned_to?.id === currentUserId &&
                c.status !== "resolved";
              const overdue = isOverdue(c, nowTick);
              const complaint = isActiveComplaint(c);
              const typingEntry = typingByPhone[c.phone];
              const someoneElseTyping =
                typingEntry &&
                (!currentUserId || typingEntry.user_id !== currentUserId);
              const viewersList = viewersByPhone[c.phone] || [];
              const otherViewers = currentUserId
                ? viewersList.filter((v) => v.user_id !== currentUserId)
                : viewersList;
              const claim = claimsByPhone[c.phone];
              const claimedByOther =
                claim && currentUserId && claim.user_id !== currentUserId;
              // Dedupe: if claim holder is also in viewers, drop them from
              // viewers — single agent shouldn't show as both "viewing" AND
              // "handling" the same chat (per user feedback 2026-02).
              const otherViewersDeduped = claim
                ? otherViewers.filter((v) => v.user_id !== claim.user_id)
                : otherViewers;
              const ch = channelMeta(c);
              // Merge typing into the green viewers badge when the typing user
              // is one of the viewers (avoids duplicate "Rahul is typing…" line + name pill).
              const typingViewer =
                someoneElseTyping
                  ? otherViewersDeduped.find((v) => v.user_id === typingEntry.user_id)
                  : null;
              const showInlineTyping = !!typingViewer;
              return (
                <li key={c.phone}>
                  <button
                    type="button"
                    onClick={() => onSelect(c.phone)}
                    data-testid={`conversation-item-${c.phone}`}
                    data-complaint={complaint ? "true" : "false"}
                    className={`w-full text-left px-3.5 py-3.5 flex gap-3 items-start transition-colors border-l-[3px] ${
                      active
                        ? "bg-[var(--bg)] border-l-[var(--fg)]"
                        : complaint
                        ? "bg-[#FEE2E2]/70 dark:bg-[#7F1D1D]/30 border-l-[#DC2626] hover:bg-[#FECACA]/80 dark:hover:bg-[#7F1D1D]/40"
                        : overdue
                        ? "bg-[#FEE2E2]/60 dark:bg-[#7F1D1D]/25 border-l-[#EF4444] hover:bg-[#FEE2E2]/80 dark:hover:bg-[#7F1D1D]/35"
                        : mine
                        ? "bg-[#FEF3C7]/40 dark:bg-[#78350F]/20 border-l-[#F59E0B] hover:bg-[#FEF3C7]/60 dark:hover:bg-[#78350F]/30"
                        : "border-l-transparent hover:bg-[var(--bg)]"
                    }`}
                  >
                    <div className="relative">
                      <Avatar className="h-11 w-11">
                        <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
                          {initials(c.name, c.phone)}
                        </AvatarFallback>
                      </Avatar>
                      {complaint ? (
                        <span
                          className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full ring-2 ring-[var(--surface)] bg-[#DC2626] complaint-dot"
                          data-testid="complaint-dot"
                          title="Active complaint"
                        />
                      ) : (
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[var(--surface)] ${
                            STATUS_DOT[c.status] || STATUS_DOT.new
                          }`}
                        />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-sm flex items-center gap-1 min-w-0 flex-1 overflow-hidden">
                          {ch.key !== "whatsapp" && (
                            <span
                              className={`text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 ${ch.badgeBg} ${ch.badgeText}`}
                              title={ch.label}
                              data-testid={`channel-badge-${ch.key}`}
                            >
                              {ch.iconText}
                            </span>
                          )}
                          <span
                            className="truncate min-w-0"
                            title={
                              (c.rodbez_name_verified && c.rodbez_customer_name)
                                ? c.rodbez_customer_name
                                : (c.name || c.phone)
                            }
                            data-testid={`contact-first-name-${c.phone}`}
                          >
                            {(() => {
                              const full = (c.rodbez_name_verified && c.rodbez_customer_name)
                                ? c.rodbez_customer_name
                                : c.name;
                              const first = (full || "").trim().split(/\s+/)[0];
                              return first || c.phone;
                            })()}
                          </span>
                          {c.bot_active && (
                            <span
                              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#10B981] text-white shrink-0 relative"
                              data-testid={`bot-handling-icon-${c.phone}`}
                              title="Bot is handling this chat"
                            >
                              <Robot size={10} weight="fill" />
                              <span className="absolute inset-0 rounded-full bg-[#10B981] opacity-50 animate-ping" />
                            </span>
                          )}
                          {c.call_request && (
                            <span
                              role={isAdmin ? "button" : undefined}
                              tabIndex={isAdmin ? 0 : undefined}
                              className={`inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#DC2626] text-white shrink-0 relative ${
                                isAdmin
                                  ? "cursor-pointer hover:bg-[#B91C1C] hover:scale-110 transition-transform"
                                  : ""
                              }`}
                              data-testid={`call-request-icon-${c.phone}`}
                              title={
                                isAdmin
                                  ? `Click to resolve call request silently (admin only) · ${c.call_request_reason || "Customer requested a call"}`
                                  : (c.call_request_reason
                                      ? `Customer requested a call · ${c.call_request_reason}`
                                      : "Customer requested a call")
                              }
                              onClick={async (e) => {
                                if (!isAdmin) return;
                                // Stop the parent tile-click (which would open the chat)
                                e.stopPropagation();
                                e.preventDefault();
                                const ok = window.confirm(
                                  `Resolve call request silently?\n\nCustomer ko koi message nahi jaayega — bas red phone icon clear ho jaayega.\n\nContinue?`
                                );
                                if (!ok) return;
                                try {
                                  await api.post(
                                    `/conversations/${c.phone}/resolve-call`,
                                  );
                                  toast.success("Call request resolved");
                                } catch (err) {
                                  const msg =
                                    err?.response?.data?.detail ||
                                    err?.message ||
                                    "Failed to resolve call";
                                  toast.error(msg);
                                }
                              }}
                            >
                              <Phone size={10} weight="fill" />
                              <span className="absolute inset-0 rounded-full bg-[#DC2626] opacity-60 animate-ping" />
                            </span>
                          )}
                          {c.rodbez_name_verified && (
                            <span
                              className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-[#10B981] text-white shrink-0"
                              data-testid="rodbez-verified-badge"
                              title={`Verified RodBez customer${c.rodbez_customer_name ? `: ${c.rodbez_customer_name}` : ""}`}
                            >
                              <CheckCircle size={11} weight="fill" />
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => openRideStatus(e, c)}
                            className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#FBBF24] text-[#78350F] shrink-0 hover:bg-[#F59E0B] focus:outline-none focus:ring-2 focus:ring-[#F59E0B] focus:ring-offset-1"
                            data-testid={`ride-status-info-btn-${c.phone}`}
                            title="Peek ride status — calls RodBez API"
                            aria-label="Show ride status"
                          >
                            <Info size={11} weight="fill" />
                          </button>
                          {complaint && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#DC2626] text-white flex items-center gap-0.5"
                              data-testid="complaint-badge"
                              title={`Complaint: ${c.complaint?.trigger || ""}`}
                            >
                              <Siren size={10} weight="fill" /> Complaint
                            </span>
                          )}
                          {!complaint && overdue && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#EF4444] text-white flex items-center gap-0.5"
                              data-testid="overdue-badge"
                              title={`Customer waiting > ${SLA_MINUTES} min`}
                            >
                              <Warning size={10} weight="fill" /> Overdue
                            </span>
                          )}
                          {mine && !overdue && !complaint && (
                            <span
                              className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#F59E0B] text-white"
                              data-testid="mine-badge"
                              title="Assigned to you"
                            >
                              Mine
                            </span>
                          )}
                        </div>
                        {c.last_timestamp && (
                          <div
                            className={`text-[11px] shrink-0 whitespace-nowrap tabular-nums font-semibold ${complaint || overdue ? "text-[#B91C1C]" : "text-[#475569] dark:text-[#94A3B8]"}`}
                            title={`Last activity: ${formatDistanceToNow(new Date(c.last_timestamp), { addSuffix: true })}`}
                            data-testid={`last-activity-${c.phone}`}
                          >
                            {shortDistance(new Date(c.last_timestamp))}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-[var(--muted)] mt-1 flex items-start gap-1.5">
                        <span
                          className={`flex-1 min-w-0 truncate leading-snug ${
                            someoneElseTyping && !showInlineTyping ? "text-[#10B981] font-medium italic" : ""
                          }`}
                          title={!someoneElseTyping ? (c.last_message || "") : undefined}
                          data-testid={someoneElseTyping && !showInlineTyping ? `typing-${c.phone}` : undefined}
                        >
                          {someoneElseTyping && !showInlineTyping ? (
                            <>
                              <span className="typing-dot-anim mr-1">•••</span>
                              {(typingEntry.user_name || "Someone").split(" ")[0]} is typing…
                            </>
                          ) : (
                            truncateWords(c.last_message, 4) || "…"
                          )}
                        </span>
                        {c.unread > 0 && (
                          <Badge className="bg-[#EF4444] hover:bg-[#EF4444] text-white h-4 px-1.5 text-[10px] shrink-0 mt-0.5">
                            {c.unread}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <div className="text-[10px] text-[var(--muted)] font-mono shrink-0">
                          {ch.key === "whatsapp" ? `+${c.phone}` : ch.addressLabel}
                        </div>
                        {c.last_timestamp && (
                          <span
                            className={`text-[10px] font-semibold shrink-0 whitespace-nowrap tabular-nums px-1.5 py-0.5 rounded ${
                              complaint || overdue
                                ? "bg-[#FEE2E2] text-[#B91C1C] dark:bg-[#7F1D1D]/30 dark:text-[#FCA5A5]"
                                : "bg-[#F1F5F9] text-[#475569] dark:bg-[#1E293B] dark:text-[#94A3B8]"
                            }`}
                            data-testid={`last-activity-meta-${c.phone}`}
                            title={`Last activity: ${formatDistanceToNow(new Date(c.last_timestamp), { addSuffix: true })}`}
                          >
                            {relativeAgo(new Date(c.last_timestamp))}
                          </span>
                        )}
                        {(() => {
                          // Prefer the server-computed `department` (which
                          // applies the 24h Completed-window rule). Fall
                          // back to a client-side classification when the
                          // SSE delta hasn't carried `department` yet —
                          // ensures the chip never gets stuck on "New"
                          // just because the most recent contact event
                          // skipped that field.
                          const status = (c.rodbez_last_ride_status || "").trim();
                          const lower = status.toLowerCase();
                          const SALES = new Set([
                            "pending", "processing", "confirmed", "cancelled",
                          ]);
                          const OPS = new Set([
                            "assigned", "arrived",
                            "in-progress", "in_progress", "inprogress",
                          ]);
                          let dept = c.department;
                          if (!dept) {
                            if (!status) dept = "New";
                            else if (SALES.has(lower)) dept = "Sales";
                            else if (OPS.has(lower)) dept = "Operations";
                            else if (lower === "completed") {
                              const ts = c.rodbez_last_ride_status_updated_at;
                              if (!ts) dept = "Sales";
                              else {
                                const ageMs =
                                  Date.now() - new Date(ts).getTime();
                                dept = ageMs < 24 * 60 * 60 * 1000
                                  ? "Operations"
                                  : "Sales";
                              }
                            } else dept = "Sales";
                          }
                          const styles = {
                            New: { bg: "#E5E7EB", fg: "#374151", label: "New" },
                            Sales: { bg: "#DBEAFE", fg: "#1E40AF", label: "Sales" },
                            Operations: { bg: "#FED7AA", fg: "#9A3412", label: "Ops" },
                          };
                          const s = styles[dept] || styles.New;
                          const tooltip = status
                            ? `${dept} · last status: ${status}`
                            : `${dept} · no ride yet`;
                          return (
                            <span
                              className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0"
                              style={{ background: s.bg, color: s.fg }}
                              data-testid={`dept-badge-${c.phone}`}
                              title={tooltip}
                            >
                              {s.label}
                            </span>
                          );
                        })()}
                        {c.assigned_to && !mine && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-semibold shrink-0 bg-[#DBEAFE] text-[#1D4ED8] dark:bg-[#1E3A8A]/40 dark:text-[#93C5FD] border border-[#3B82F6]/30"
                            title={`Assigned to ${c.assigned_to.full_name || c.assigned_to.email}`}
                            data-testid={`assigned-pill-${c.phone}`}
                          >
                            @{(c.assigned_to.full_name || c.assigned_to.email || "").split("@")[0]}
                          </span>
                        )}
                        {mine && overdue && (
                          <span className="text-[10px] px-1.5 rounded bg-[#F59E0B] text-white font-medium shrink-0">
                            You
                          </span>
                        )}
                        {/* Bot handling visual is rendered inline next to
                            the customer name (small green Robot icon). The
                            old big "🤖 Bot handling" pill below is no longer
                            shown — but the bot_active flag still suppresses
                            the green "viewing"/"handling" pills below so we
                            don't double-up. */}
                        {!c.bot_active && otherViewersDeduped.length > 0 && (
                          <span
                            className="flex items-center gap-1 text-[10px] text-[#065F46] dark:text-[#A7F3D0] font-semibold px-1.5 py-0.5 rounded-full bg-[#D1FAE5] dark:bg-[#064E3B]/60 border border-[#10B981]/40 max-w-full"
                            data-testid={`viewers-${c.phone}`}
                            title={
                              showInlineTyping
                                ? `${typingViewer.user_name} is typing right now — please don't enter`
                                : `Currently viewing: ${otherViewersDeduped.map((v) => v.user_name).join(", ")} — please don't enter to avoid double-handling`
                            }
                          >
                            {showInlineTyping ? (
                              <PencilSimple size={10} weight="fill" className="shrink-0 animate-pulse" />
                            ) : (
                              <span className="relative flex h-1.5 w-1.5 shrink-0">
                                <span className="absolute inset-0 rounded-full bg-[#10B981] opacity-75 animate-ping" />
                                <span className="relative h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                              </span>
                            )}
                            <span className="truncate">
                              {showInlineTyping
                                ? `${(typingViewer.user_name || "Someone").split(" ")[0]} typing…`
                                : otherViewersDeduped.length === 1
                                ? `${(otherViewersDeduped[0].user_name || "Someone").split(" ")[0]} viewing`
                                : `${(otherViewersDeduped[0].user_name || "Someone").split(" ")[0]} +${otherViewersDeduped.length - 1} viewing`}
                            </span>
                          </span>
                        )}
                        {!c.bot_active && claimedByOther && (
                          <span
                            className="flex items-center gap-1 text-[10px] text-[#B45309] dark:text-[#FBBF24] font-semibold px-1.5 py-0.5 rounded-full bg-[#FEF3C7] dark:bg-[#78350F]/40 border border-[#F59E0B]/40 max-w-full"
                            data-testid={`claim-${c.phone}`}
                            title={`${claim.user_name} is handling this chat`}
                          >
                            <Lock size={10} weight="fill" className="shrink-0" />
                            <span className="truncate">
                              {(claim.user_name || "").split(" ")[0]} handling
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </ScrollArea>

      {/* Ride status peek dialog (yellow-info-icon click) */}
      <Dialog
        open={!!rideStatus?.open}
        onOpenChange={(o) => setRideStatus((s) => (s ? { ...s, open: o } : s))}
      >
        <DialogContent className="sm:max-w-md" data-testid="ride-status-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FBBF24] text-[#78350F]"
                aria-hidden
              >
                <Info size={13} weight="fill" />
              </span>
              Ride status
            </DialogTitle>
            <DialogDescription>
              {rideStatus?.contact
                ? `${
                    (rideStatus.contact.rodbez_name_verified &&
                      rideStatus.contact.rodbez_customer_name) ||
                    rideStatus.contact.name ||
                    rideStatus.contact.phone
                  } · +${rideStatus.contact.phone}`
                : ""}
            </DialogDescription>
          </DialogHeader>

          {rideStatus?.loading && (
            <div className="flex items-center gap-2 text-sm text-[var(--muted)] py-6 justify-center">
              <CircleNotch size={16} className="animate-spin" />
              Fetching latest ride from RodBez…
            </div>
          )}

          {!rideStatus?.loading && rideStatus?.error && (
            <div
              className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2"
              data-testid="ride-status-error"
            >
              {rideStatus.error}
            </div>
          )}

          {!rideStatus?.loading && !rideStatus?.error && !rideStatus?.data && (
            <div
              className="text-sm text-[var(--muted)] py-4 text-center"
              data-testid="ride-status-none"
            >
              No active ride found for this customer.
            </div>
          )}

          {!rideStatus?.loading && !rideStatus?.error && rideStatus?.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2 py-1.5">
                  <div className="text-[10px] uppercase text-[var(--muted)]">Ride ID</div>
                  <div className="font-semibold truncate">{rideStatus.data.bookingNo || "—"}</div>
                </div>
                <div className="bg-[var(--bg)] border border-[var(--border)] rounded-md px-2 py-1.5">
                  <div className="text-[10px] uppercase text-[var(--muted)]">Status</div>
                  <div className="font-semibold truncate">{rideStatus.data.status || "—"}</div>
                </div>
              </div>
              <pre
                className="whitespace-pre-wrap text-xs leading-relaxed bg-[var(--bg)] border border-[var(--border)] rounded-md p-3 max-h-[260px] overflow-y-auto font-sans"
                data-testid="ride-status-text"
              >
                {rideStatus.text || JSON.stringify(rideStatus.data, null, 2)}
              </pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </aside>
  );
}
